/**
 * OrejaCommitSummary - Summary display after committing Oreja changes to ClickUp.
 *
 * Shows a confirmation header, counts of updated/created tasks, a list of
 * individual changes with ClickUp links, failed tasks with retry, and a
 * button to start a new session.
 */

import { CheckCircle2, ExternalLink, AlertTriangle, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { OrejaCommittedChange, OrejaFailedCommit } from '@/lib/oreja/types';

export interface OrejaCommitSummaryProps {
  /** List of changes that were committed to ClickUp. */
  committedChanges: OrejaCommittedChange[];
  /** Tasks that failed to push during commit. */
  failedCommits?: OrejaFailedCommit[];
  /** Callback to retry only the failed commits. */
  onRetryFailed?: () => void;
  /** Whether a retry is currently in progress. */
  isRetrying?: boolean;
  /** Callback to start a new Oreja session. */
  onStartNew: () => void;
}

/**
 * Computes a human-readable summary line from committed changes.
 *
 * @param changes - Array of committed changes
 * @returns Summary string like "Updated 3 tasks, created 1 new task"
 */
function buildSummaryLine(changes: OrejaCommittedChange[]): string {
  const updated = changes.filter((c) => c.type === 'update').length;
  const created = changes.filter((c) => c.type === 'new_task').length;

  const parts: string[] = [];
  if (updated > 0) {
    parts.push(`Updated ${updated} task${updated !== 1 ? 's' : ''}`);
  }
  if (created > 0) {
    parts.push(`created ${created} new task${created !== 1 ? 's' : ''}`);
  }

  if (parts.length === 0) return 'No changes committed';
  // Capitalize the first part, join with comma
  parts[0] = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
  return parts.join(', ');
}

/**
 * Displays a summary of committed Oreja changes with ClickUp links,
 * failed tasks with error details, and a retry button for failures.
 *
 * @param props - Component props with committed changes, failures, and callbacks
 * @returns Card component with change summary and action buttons
 */
export function OrejaCommitSummary({
  committedChanges,
  failedCommits = [],
  onRetryFailed,
  isRetrying = false,
  onStartNew,
}: OrejaCommitSummaryProps) {
  const hasFailures = failedCommits.length > 0;
  const hasSuccesses = committedChanges.length > 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          {hasFailures ? (
            <>
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Partially Committed
            </>
          ) : (
            <>
              <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              Changes Committed
            </>
          )}
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          {buildSummaryLine(committedChanges)}
          {hasFailures && `, ${failedCommits.length} failed`}
        </p>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Successful pushes */}
        {hasSuccesses && committedChanges.map((change) => (
          <div
            key={change.clickup_task_id}
            className="flex items-start justify-between gap-2 rounded-md border px-3 py-2 text-sm"
          >
            <div className="min-w-0 flex-1">
              <p className="font-medium truncate">{change.task_title}</p>
              <p className="text-muted-foreground text-xs break-words">
                {change.changes_summary}
              </p>
            </div>
            <a
              href={change.clickup_url}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={`Open ${change.task_title} in ClickUp`}
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        ))}

        {/* Failed pushes */}
        {hasFailures && (
          <>
            <div className="pt-1">
              <p className="text-sm font-medium text-destructive">
                Failed to push ({failedCommits.length})
              </p>
            </div>
            <div className="max-h-64 overflow-y-auto space-y-2">
            {failedCommits.map((failed) => (
              <div
                key={`${failed.type}-${failed.index}`}
                className="flex items-start justify-between gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{failed.title}</p>
                  <p className="text-destructive text-xs break-words">{failed.error}</p>
                </div>
              </div>
            ))}
            </div>
            {onRetryFailed && (
              <Button
                variant="outline"
                onClick={onRetryFailed}
                disabled={isRetrying}
                className="w-full"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                {isRetrying
                  ? 'Retrying...'
                  : `Retry Failed (${failedCommits.length})`}
              </Button>
            )}
          </>
        )}

        <Button onClick={onStartNew} className="w-full mt-2">
          Start New Update
        </Button>
      </CardContent>
    </Card>
  );
}
